import { useState } from 'react';

import { Button } from '../../button';

import {
  answerReturned,
  COMPACT_COUNTEREXAMPLE,
  COMPACT_REFINEMENT,
  compactReplayed,
  compactSummary,
  compactSummaryFact,
  compactSummaryText,
  compactTokens,
  compactTransition,
  DROPPED_TEXT,
  folded,
  HOMES,
  homeLabels,
  IDEMPOTENT_COUNTEREXAMPLE,
  IDEMPOTENT_REFINEMENT,
  idempotentSummary,
  idempotentTransition,
  initialCompact,
  initialIdempotent,
  initialResume,
  initialRows,
  modelCalls,
  modelRuns,
  requests,
  RESUME_REFINEMENT,
  resumeRuns,
  resumeSummary,
  resumeTransition,
  ROWS_COUNTEREXAMPLE,
  ROWS_REFINEMENT,
  rowsAdded,
  rowsSummary,
  rowsTransition,
  rowsWritten,
  SENDS,
  sendLabels,
  sentText,
  toHome,
  toolRows,
  toSend,
  toTurn,
  toTurnCount,
  TURN_COUNTS,
  turnLabels,
  turnRows,
  TURNS,
  type CompactAction,
  type IdempotentAction,
  type ResumeAction,
  type RowsAction,
} from './conversation-models';
import { Lab, Panel, Reset, Result } from './lab-parts';
import { ModelSelect } from './learning-model-surface';

import './relationship-labs.css';
import './conversation-labs.css';

function Predict({ children }: { children: string }) {
  return (
    <p className="rel-predict">
      <strong>Predict first</strong> {children}
    </p>
  );
}
function Table({
  label,
  columns,
  rows,
  template = `repeat(${columns.length}, minmax(0, 1fr))`,
}: {
  label: string;
  columns: string[];
  rows: (string | number)[][];
  template?: string;
}) {
  return (
    <div className="rel-table-scroll">
      <div className="rel-table cb-record-grid" role="table" aria-label={label}>
        <div
          role="row"
          className="rel-row rel-table-head"
          style={{ gridTemplateColumns: template }}
        >
          {columns.map((column) => (
            <span key={column} role="columnheader">
              {column}
            </span>
          ))}
        </div>
        {rows.length ? (
          rows.map((row, index) => (
            <div
              role="row"
              className="rel-row"
              key={index}
              style={{ gridTemplateColumns: template }}
            >
              {row.map((cell, column) => (
                <span role="cell" key={column}>
                  {cell}
                </span>
              ))}
            </div>
          ))
        ) : (
          <div role="row" className="rel-row">
            <span role="cell">No rows</span>
          </div>
        )}
      </div>
    </div>
  );
}
function Facts({ values }: { values: [string, string][] }) {
  return (
    <dl className="cb-memory">
      {values.map(([label, value]) => (
        <div key={label}>
          <dt>{label}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  );
}
const REPLAY = 'This graphic replays real runs.';

export function StateRowsLab() {
  const [state, setState] = useState(initialRows);
  const act = (action: RowsAction) => setState((current) => rowsTransition(current, action));
  const summary = rowsSummary(state);
  return (
    <Lab
      title="What does one turn add to the table?"
      eyebrow="State · a row for every message"
      className="rel-lab conversation-lab"
    >
      <Predict>
        What does one turn add to the table? Choose a Turn, then press Store the turn.
      </Predict>
      <div className="cb-controls">
        <label>
          Turn
          <ModelSelect
            aria-label="Turn"
            value={state.turn}
            onValueChange={(value) => act({ type: 'turn', value: toTurn(value) })}
            options={TURNS.map((turn) => ({ value: turn, label: turnLabels[turn] }))}
          />
        </label>
        <Button size="sm" disabled={state.stored} onClick={() => act({ type: 'store' })}>
          Store the turn
        </Button>
        <Reset onClick={() => act({ type: 'reset' })} />
      </div>
      <Table
        label="Rows added"
        columns={['Position', 'Role', 'Content or request']}
        template="minmax(0, 0.6fr) minmax(0, 0.8fr) minmax(0, 3fr)"
        rows={
          state.stored
            ? turnRows[state.turn].map((row) => [row.position, row.role, row.content])
            : []
        }
      />
      {state.stored ? (
        <Facts
          values={[
            ['Rows added', String(rowsAdded(state.turn))],
            ['Model calls', String(modelCalls(state.turn))],
            ['Tool rows', String(toolRows(state.turn))],
          ]}
        />
      ) : null}
      <Result tone={summary.tone}>{summary.message}</Result>
      {state.stored ? (
        <p className="rel-caption">
          {ROWS_COUNTEREXAMPLE} {ROWS_REFINEMENT}
        </p>
      ) : null}
      <p className="rel-caption">{REPLAY}</p>
    </Lab>
  );
}

export function StateResumeLab() {
  const [state, setState] = useState(initialResume);
  const act = (action: ResumeAction) => setState((current) => resumeTransition(current, action));
  const run = resumeRuns[state.home];
  const summary = resumeSummary(state);
  return (
    <Lab
      title="Does the assistant remember after a reload?"
      eyebrow="State · resuming is replaying"
      className="rel-lab conversation-lab"
    >
      <Predict>
        Does the assistant remember after a reload? Choose where the conversation lives, then press
        Reload, then ask who owns the first one.
      </Predict>
      <div className="cb-controls">
        <label>
          Where the conversation lives
          <ModelSelect
            aria-label="Where the conversation lives"
            value={state.home}
            onValueChange={(value) => act({ type: 'home', value: toHome(value) })}
            options={HOMES.map((home) => ({ value: home, label: homeLabels[home] }))}
          />
        </label>
        <Button size="sm" disabled={state.reloaded} onClick={() => act({ type: 'reload' })}>
          Reload, then ask who owns the first one
        </Button>
        <Reset onClick={() => act({ type: 'reset' })} />
      </div>
      <Panel label="Messages sent to the model">
        <pre aria-label="Messages sent to the model">
          {state.reloaded ? sentText(state.home) : '(not reloaded yet)'}
        </pre>
      </Panel>
      <Panel label="The answer">
        <pre aria-label="The answer">{state.reloaded ? run.answer : '(not reloaded yet)'}</pre>
      </Panel>
      {state.reloaded ? (
        <Facts
          values={[
            ['Messages replayed', String(run.replayed)],
            ['Lookups made', String(run.lookups)],
            ['Answered', run.answered],
          ]}
        />
      ) : null}
      <Result tone={summary.tone}>{summary.message}</Result>
      {summary.tone === 'warning' ? <p className="rel-caption">{RESUME_REFINEMENT}</p> : null}
      <p className="rel-caption">{REPLAY}</p>
    </Lab>
  );
}

export function StateCompactLab() {
  const [state, setState] = useState(initialCompact);
  const act = (action: CompactAction) => setState((current) => compactTransition(current, action));
  const summary = compactSummary(state);
  return (
    <Lab
      title="What does a summary save, and what does it lose?"
      eyebrow="State · shorten the past"
      className="rel-lab conversation-lab"
    >
      <Predict>
        What does a summary save and lose? Choose Turns so far, then press Summarize the earlier
        turns.
      </Predict>
      <div className="cb-controls">
        <label>
          Turns so far
          <ModelSelect
            aria-label="Turns so far"
            value={state.turns}
            onValueChange={(value) => act({ type: 'turns', value: toTurnCount(value) })}
            options={TURN_COUNTS.map((count) => ({ value: count, label: count }))}
          />
        </label>
        <Button
          size="sm"
          variant="secondary"
          aria-pressed={state.summarize}
          onClick={() => act({ type: 'toggle-summary' })}
        >
          Summarize the earlier turns
        </Button>
        <Reset onClick={() => act({ type: 'reset' })} />
      </div>
      <Panel label="The summary row">
        <pre aria-label="The summary row">{compactSummaryText(state)}</pre>
      </Panel>
      <Facts
        values={[
          ['Prompt tokens of the next call', compactTokens(state)],
          ['Messages replayed', String(compactReplayed(state))],
          ['Summary', compactSummaryFact(state)],
        ]}
      />
      <Result tone={summary.tone}>{summary.message}</Result>
      {folded(state) ? (
        <p className="rel-caption">
          {DROPPED_TEXT} {COMPACT_COUNTEREXAMPLE}
        </p>
      ) : null}
      {state.turns === '6' ? <p className="rel-caption">{COMPACT_REFINEMENT}</p> : null}
      <p className="rel-caption">{REPLAY}</p>
    </Lab>
  );
}

export function StateIdempotentLab() {
  const [state, setState] = useState(initialIdempotent);
  const act = (action: IdempotentAction) =>
    setState((current) => idempotentTransition(current, action));
  const summary = idempotentSummary(state);
  return (
    <Lab
      title="What happens when the same turn is sent twice?"
      eyebrow="State · not twice"
      className="rel-lab conversation-lab"
    >
      <Predict>
        What happens when the same turn is sent twice? Choose what happens, then press Send.
      </Predict>
      <div className="cb-controls">
        <label>
          What happens
          <ModelSelect
            aria-label="What happens"
            value={state.send}
            onValueChange={(value) => act({ type: 'send', value: toSend(value) })}
            options={SENDS.map((send) => ({ value: send, label: sendLabels[send] }))}
          />
        </label>
        <Button
          size="sm"
          variant="secondary"
          aria-pressed={state.keyed}
          onClick={() => act({ type: 'toggle-key' })}
        >
          Idempotency key
        </Button>
        <Button size="sm" disabled={state.sent} onClick={() => act({ type: 'run' })}>
          Send
        </Button>
        <Reset onClick={() => act({ type: 'reset' })} />
      </div>
      <Table
        label="Requests"
        columns={['Request', 'Key', 'The server']}
        template="minmax(0, 1fr) minmax(0, 0.7fr) minmax(0, 2.4fr)"
        rows={
          state.sent
            ? requests(state.send, state.keyed).map((row) => [row.request, row.key, row.server])
            : []
        }
      />
      {state.sent ? (
        <Facts
          values={[
            ['Model runs', String(modelRuns(state.send, state.keyed))],
            ['Rows written', String(rowsWritten(state.send, state.keyed))],
            ['Answer returned', answerReturned(state.send, state.keyed)],
          ]}
        />
      ) : null}
      <Result tone={summary.tone}>{summary.message}</Result>
      {summary.tone === 'warning' ? (
        <p className="rel-caption">
          {IDEMPOTENT_COUNTEREXAMPLE} {IDEMPOTENT_REFINEMENT}
        </p>
      ) : null}
      <p className="rel-caption">{REPLAY}</p>
    </Lab>
  );
}
